/**
 * Modal store
 */
import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import { ModalTypeEnum, StorageKeysEnum } from '@star/constants';
import { cloneDeep, concat, filter, find, forEach, get, includes, isObject, map, pick, some } from 'lodash-es';
import { promiseTimeout, useLocalStorage, useSessionStorage } from '@vueuse/core';
import { emitter, EventEnum } from '@star/utils';
import { storeToRefs, useActivityStore, useTenantStore, useUserStore } from '../../index';
import dayjs from 'dayjs';

type ModalItem = {
  type: ModalTypeEnum;
  id: string | number;
  show: boolean;
  sort: number;
  info?: Record<string, any>;
};

type ModalStatus = {
  date?: string;
  today?: boolean;
  forever?: boolean;
  preLoginModal?: 'NO_POPUP' | 'DAILY_ONCE' | 'HIGH_FREQUENCY' | '';
  postLoginModal?: 'NO_POPUP' | 'DAILY_ONCE' | 'HIGH_FREQUENCY' | 'EVERY_LOGIN' | 'ONLY_ONCE' | '';
  popped?: boolean;
  info?: Record<string, any>;
};

// 需要配置弹窗的活动类型
const activityTypeMps = {
  '1': ModalTypeEnum.DEPOSIT_BONUS,
  '8': ModalTypeEnum.LOTTERY_WHEEL,
};

const DEFAULT_MODAL_LIST: ModalItem[] = [
  {
    type: ModalTypeEnum.LOGIN_REGISTER_MODAL,
    id: ModalTypeEnum.LOGIN_REGISTER_MODAL,
    show: false,
    sort: 999,
  },
  {
    type: ModalTypeEnum.NOTICE_MODAL,
    id: ModalTypeEnum.NOTICE_MODAL,
    show: false,
    sort: 998,
  },
  {
    type: ModalTypeEnum.REGISTER_BONUS_MODAL,
    id: ModalTypeEnum.REGISTER_BONUS_MODAL,
    show: false,
    sort: 997,
  },
  {
    type: ModalTypeEnum.BOTTOM_DOWNLOAD_MODAL,
    id: ModalTypeEnum.BOTTOM_DOWNLOAD_MODAL,
    show: false,
    sort: 2,
  },
  {
    type: ModalTypeEnum.TOP_DOWNLOAD_MODAL,
    id: ModalTypeEnum.TOP_DOWNLOAD_MODAL,
    show: false,
    sort: 1,
  },
];

const DEFAULT_MODAL_STATUS_DATA: ModalStatus = {
  date: '', // 弹窗日期
  today: false, // 今日不在弹窗
  forever: false, // 永不弹窗
  preLoginModal: '', // 登录前弹窗类型
  postLoginModal: '', // 登录后弹窗类型
  popped: false, // 今日是否已弹窗过
};

export const useModalStore = defineStore('modal', () => {
  const userStore = useUserStore();
  const { isRepairing } = storeToRefs(useTenantStore());
  const { isLogin, getUserInfo, signInList } = storeToRefs(userStore);
  const { getRechareInfo } = useActivityStore();

  const isShowTopDownload = ref<boolean>(false); // 是否已显示过顶部下载
  const isShowBottomDownload = ref<boolean>(false); // 是否已显示过底部下载

  const showLoginRegister = ref<boolean>(false); // 显示登录注册
  const showTopDownload = ref<boolean>(false); // 显示顶部下载
  const showBottomDownload = ref<boolean>(false); // 显示底部下载
  const showRegisterBonus = ref<boolean>(false); // 注册红包弹窗
  const showDepositBonusModal = ref<boolean>(false); // 充值奖励弹窗
  const showSignInModal = ref<boolean>(false); // 签到弹窗
  const showLotteryWheelModal = ref<boolean>(false); // 幸运转盘弹窗
  const showNotice = ref<boolean>(false); // 公告弹窗
  const showHorseman = ref<boolean>(false); // 跑马灯
  const recoverPasswordModal = ref<boolean>(false); // 找回密码

  // 首页弹窗列表
  const modalList = useSessionStorage<ModalItem[]>(StorageKeysEnum.POPUP_MODAL_LIST, []);
  // 弹窗状态
  const modalStatusMap = useLocalStorage<Record<number, any>>(StorageKeysEnum.POPUP_MODAL_STATUS_MAP, {
    // 未登录状态
    0: {},
  });

  // 当前打开的弹窗
  const activeModal = ref<Record<string, any>>();

  // 当前登录用户ID  0:未登录
  const userId = computed(() => getUserInfo.value.cust_id || 0);

  const activeModalStatusMap = computed(() => {
    const statusMap = isLogin.value ? modalStatusMap.value?.[userId.value] : modalStatusMap.value?.[0];
    console.log(statusMap, 'statusMap');
    return statusMap;
  });

  // 当前是否有弹窗打开
  const isShowModal = computed(() => {
    // console.log(showLoginRegister.value, 'initModal showLoginRegister');
    // console.log(showBottomDownload.value, 'initModal showBottomDownload');
    // console.log(showRegisterBonus.value, 'initModal showRegisterBonus');
    // console.log(showNotice.value, 'initModal showNotice');
    // console.log(showHorseman.value, 'initModal showHorseman');
    // console.log(showDepositBonusModal.value, 'initModal showDepositBonusModal');
    // console.log(showSignInModal.value, 'initModal showSignInModal');
    // console.log(showLotteryWheelModal.value, 'initModal showLotteryWheelModal');
    return some(
      [
        showBottomDownload.value,
        showRegisterBonus.value,
        showNotice.value,
        showHorseman.value,
        showDepositBonusModal.value,
        showSignInModal.value,
        showLotteryWheelModal.value,
      ],
      val => val
    );
  });

  const _getParams = (val?: ModalTypeEnum | Pick<ModalItem, 'type' | 'id'>) => {
    return isObject(val) ? val : { type: val, id: val };
  };

  const _getKey = (type: ModalTypeEnum, id: number | string) => {
    return type === id ? type : `${type}-${id}`;
  };

  const setModal = (val: ModalTypeEnum | Pick<ModalItem, 'type' | 'id'>, visible: boolean) => {
    const { type } = _getParams(val);
    console.log(type, visible, 'initModal setModal');
    switch (type) {
      case ModalTypeEnum.LOGIN_REGISTER_MODAL:
        if (showLoginRegister.value && visible) return;
        const info: any = get(find(modalList.value, { type: ModalTypeEnum.LOGIN_REGISTER_MODAL }), 'info');
        emitter.emit(EventEnum.LOGIN, visible ? { type: 1, ...info } : { type: 0 });
        showLoginRegister.value = visible;
        break;
      case ModalTypeEnum.NOTICE_MODAL:
        showNotice.value = visible;
        break;
      case ModalTypeEnum.HORSEMAN_MODAL:
        showHorseman.value = visible;
        break;
      case ModalTypeEnum.REGISTER_BONUS_MODAL:
        showRegisterBonus.value = visible;
        break;
      case ModalTypeEnum.DEPOSIT_BONUS:
        showDepositBonusModal.value = visible;
        break;
      case ModalTypeEnum.SIGN_IN:
        showSignInModal.value = visible;
        break;
      case ModalTypeEnum.LOTTERY_WHEEL:
        showLotteryWheelModal.value = visible;
        break;
      case ModalTypeEnum.TOP_DOWNLOAD_MODAL:
        showTopDownload.value = visible;
        if (!visible) {
          isShowTopDownload.value = true;
        }
        break;
      case ModalTypeEnum.BOTTOM_DOWNLOAD_MODAL:
        showBottomDownload.value = visible;
        if (!visible) {
          isShowBottomDownload.value = true;
        }
        break;
      case ModalTypeEnum.RECOVER_PASSWORD:
        recoverPasswordModal.value = visible;
        break;
    }
  };

  const showModal = (val: ModalTypeEnum | Pick<ModalItem, 'type' | 'id'>) => {
    setModal(val, true);
  };

  const hideModal = async (val: ModalTypeEnum | Pick<ModalItem, 'type' | 'id'>, isBreak: boolean = false) => {
    console.log(val, isBreak, 'initModal hideModal');
    setModal(val, false);
    clearPopupModal(val);
    if (!isBreak) {
      await promiseTimeout(300);
      console.log(modalList.value, 'initModal hideModal modalList');
      initModal();
    }
  };

  const addPopupModal = (type: ModalTypeEnum, info?: Record<string, any>) => {
    forEach(modalList.value, item => {
      if (item.type === type) {
        const modalStatus = getModalStatus(type);
        console.log(type, modalStatus, 'addPopupModal');
        item.show = modalStatus?.forever || modalStatus?.today ? !1 : !0;
        if (info) {
          item.info = { ...item.info, ...info };
        }
      }
    });
  };

  const clearPopupModal = (val?: ModalTypeEnum | Pick<ModalItem, 'type' | 'id'>) => {
    const { id } = _getParams(val);
    if (id) {
      forEach(modalList.value, item => {
        if (item.id == id) {
          item.show = false;
          const modalStatus = getModalStatus({ type: item.type, id: item.id });
          const popupType = isLogin.value ? modalStatus?.postLoginModal : modalStatus?.preLoginModal;
          setModalStatus(
            {
              type: item.type,
              id: item.id,
            },
            { popped: popupType !== 'HIGH_FREQUENCY' }
          );
        }
      });
    } else {
      modalList.value = map(modalList.value, item => ({ ...item, show: false }));
    }
  };

  const initModal = async (modalType?: ModalTypeEnum) => {
    const modal = find(modalList.value, item => item.show && item.type !== modalType);
    console.log(cloneDeep(modalList.value), 'initModal');
    console.log(cloneDeep(modal), isRepairing.value, isShowModal.value, 'initModal');
    if (!!modal && !isRepairing.value && !isShowModal.value) {
      activeModal.value = cloneDeep(modal);
      console.log(modal, 'modal initModal');
      showModal(modal);

      // 设置弹窗状态
      const popupType = isLogin.value ? modal.info?.actionCfg?.postLoginModal : modal.info?.actionCfg?.preLoginModal;
      console.log(popupType, 'initModal popupType');
      setModalStatus(
        {
          type: modal.type,
          id: modal.id,
        },
        {
          date: dayjs().format('YYYY-MM-DD'),
          popped: modal.type !== ModalTypeEnum.LOGIN_REGISTER_MODAL ? popupType !== 'HIGH_FREQUENCY' : isLogin.value,
        }
      );

      if (modal.type === ModalTypeEnum.TOP_DOWNLOAD_MODAL) {
        await promiseTimeout(300);
        await initModal(ModalTypeEnum.TOP_DOWNLOAD_MODAL);
      }
    }
  };

  const getPopupModalInfo = (val: ModalTypeEnum | Pick<ModalItem, 'type' | 'id'>) => {
    const { type, id } = _getParams(val);
    return get(find(modalList.value, { type, id: `${id}` }), 'info');
  };

  // 设置弹窗状态
  const setModalStatus = (val: ModalTypeEnum | Pick<ModalItem, 'type' | 'id'>, option: ModalStatus) => {
    const { type, id } = _getParams(val) as any;
    const mapKey = _getKey(type, id);
    if (mapKey === ModalTypeEnum.DEPOSIT_BONUS) {
      // 无活动ID时更新首充奖励弹窗状态
      if (modalStatusMap.value[userId.value]) {
        forEach(modalStatusMap.value[userId.value], (val, key) => {
          if (
            activityTypeMps[val.info?.activityType] === ModalTypeEnum.DEPOSIT_BONUS &&
            val.info?.actionCfg?.activityConditions === '1'
          ) {
            const key = _getKey(mapKey, val.info.id);
            modalStatusMap.value[userId.value][key] = {
              ...modalStatusMap.value[userId.value][key],
              ...option,
            };
          }
        });
      }
    } else {
      modalStatusMap.value[userId.value] = modalStatusMap.value[userId.value] || {};
      modalStatusMap.value[userId.value][mapKey] = {
        ...(modalStatusMap.value[userId.value][mapKey] || { ...DEFAULT_MODAL_STATUS_DATA }),
        ...option,
      };
    }
  };

  // 获取弹窗状态
  const getModalStatus = (val: ModalTypeEnum | Pick<ModalItem, 'type' | 'id'>) => {
    const { type, id } = _getParams(val) as any;
    const mapKey = _getKey(type, id);
    return activeModalStatusMap.value?.[mapKey];
  };

  // 初始化弹窗列表
  /**
   * 不弹窗 NO_POPUP
   * 每天弹一次 DAILY_ONCE
   * 高频弹窗 HIGH_FREQUENCY
   * 每次登录 EVERY_LOGIN
   * 只弹一次 ONLY_ONCE
   * */
  const initModalList = async () => {
    if (isRepairing.value) return;
    const { getActiveActivityModalList } = useActivityStore();
    const list: Record<string, any>[] = await getActiveActivityModalList();
    console.log(list, 'getActivityList list');
    // 筛选有弹窗的活动并解析活动配置
    let result: any[] = filter(
      map(list, item => {
        return {
          ...item,
          actionCfg: item.actionCfg ? JSON.parse(item.actionCfg) : item.actionCfg,
        };
      }),
      (item: any) => {
        return (
          !!activityTypeMps[item.activityType] &&
          item.isShowIndex === '0' &&
          ((item.actionCfg?.postLoginModal && item.actionCfg.postLoginModal !== 'NO_POPUP') ||
            (item.actionCfg?.preLoginModal && item.actionCfg.preLoginModal !== 'NO_POPUP'))
        );
      }
    );

    // 处理已领取过首充奖励的数据
    if (isLogin.value) {
      const depositActivity = find(result, item => {
        return (
          activityTypeMps[item.activityType] === ModalTypeEnum.DEPOSIT_BONUS &&
          item.actionCfg?.activityConditions === '1'
        );
      });
      if (depositActivity) {
        const state = await getDepositAwardStatus(depositActivity.id);
        console.log(state, 'getActivityList state');
        if (includes([0, 5], state)) {
          result = filter(result, item => item.id !== depositActivity.id);
          setModalStatus(
            {
              type: ModalTypeEnum.DEPOSIT_BONUS,
              id: depositActivity.id,
            },
            { forever: true }
          );
        }
      }
    }

    // 更新弹窗状态
    forEach(result, (item: any) => {
      const type = activityTypeMps[item.activityType];
      const modalStatus = getModalStatus({ type, id: item.id });
      const popupType = isLogin.value ? item.actionCfg?.postLoginModal : item.actionCfg?.preLoginModal;
      const isToday = modalStatus?.date && dayjs().isSame(modalStatus.date, 'day');
      const todayChecked = isToday ? !!modalStatus?.today : !1;
      let popped =
        popupType === 'HIGH_FREQUENCY' || (!isToday && popupType === 'DAILY_ONCE') ? !1 : !!modalStatus?.popped;
      console.log(modalStatus?.forever, todayChecked, popupType, 'getActivityList 更新弹窗状态');
      setModalStatus(
        {
          type,
          id: item.id,
        },
        {
          ...pick(item.actionCfg, ['postLoginModal', 'preLoginModal']),
          today: todayChecked,
          popped: modalStatus?.forever || todayChecked || popupType === 'NO_POPUP' ? !0 : popped,
          info: {
            ...pick(item, ['activityType', 'id']),
            actionCfg: pick(item.actionCfg, ['activityConditions']),
          },
        }
      );
    });

    // 根据弹窗状态是否显示弹窗
    result = map(result, (item: any) => {
      const type = activityTypeMps[item.activityType];
      const modalStatus = getModalStatus({ type, id: item.id });
      console.log(type, modalStatus, 'getActivityList modalStatus');
      return {
        type,
        id: `${item.id}`,
        show: !modalStatus?.popped,
        sort: 100,
        info: cloneDeep(item),
      };
    });
    const signInData = await _getSignInModal();
    modalList.value = concat(DEFAULT_MODAL_LIST, result, signInData).sort((a, b) => b.sort - a.sort);
    console.log(modalList.value, 'getActivityList modalList ---');
  };

  // 生成签到弹窗数据
  const _getSignInModal = async () => {
    const data = await userStore.reqSignIn();
    console.log(data, signInList.value, 'getActivityList signInData');
    const modalStatus = getModalStatus(ModalTypeEnum.SIGN_IN);
    const popupType = isLogin.value ? data?.after_login_modal : data?.before_login_modal;
    let show = false;
    if (!modalStatus?.forever) {
      if (!modalStatus?.today) {
        if (!signInList.value?.isSignIn) {
          if (modalStatus?.popped) {
            show = popupType === 'HIGH_FREQUENCY';
          } else {
            show = popupType !== 'NO_POPUP';
          }
        }
      }
    }
    console.log(modalStatus, 'getActivityList modalStatus');
    setModalStatus(ModalTypeEnum.SIGN_IN, {
      preLoginModal: data?.before_login_modal || '',
      postLoginModal: data?.after_login_modal || '',
      today:
        !modalStatus?.date || dayjs().isAfter(modalStatus.date, 'day')
          ? !1
          : signInList.value?.isSignIn || !!modalStatus?.today,
      popped:
        modalStatus?.forever || modalStatus?.forever
          ? !0
          : !modalStatus?.date || dayjs().isAfter(modalStatus.date, 'day')
            ? !1
            : !!modalStatus?.popped,
    });
    return Promise.resolve({
      type: ModalTypeEnum.SIGN_IN,
      id: ModalTypeEnum.SIGN_IN,
      show,
      sort: 996,
      info: cloneDeep({
        ...data,
        actionCfg: {
          ...data?.actionCfg,
          preLoginModal: data?.before_login_modal || '',
          postLoginModal: data?.after_login_modal || '',
        },
      }),
    });
  };

  // 路由切换后的弹窗状态
  const setHighFrequencyModalStatus = () => {
    forEach(modalList.value, item => {
      const popupType = isLogin.value ? item.info?.actionCfg?.postLoginModal : item.info?.actionCfg?.preLoginModal;
      // if (!isLogin.value && item.type === ModalTypeEnum.LOGIN_REGISTER_MODAL) {
      //   item.show = !1;
      // }
      if (popupType === 'HIGH_FREQUENCY') {
        setModalStatus({ type: item.type, id: item.id }, { popped: !1 });
        const modalStatus = getModalStatus({ type: item.type, id: item.id });
        if (!modalStatus?.forever) {
          if (!modalStatus?.today) {
            item.show = !modalStatus?.popped;
          }
        }
      }
    });
    console.log(cloneDeep(modalList.value), 'initModal setHighFrequencyModalStatus');
  };

  // 设置每次登录后弹窗
  const setEveryLoginModalStatus = async () => {
    if (!isLogin.value) return;
    const defaultTypes = map(DEFAULT_MODAL_LIST, item => item.type);
    console.log(cloneDeep(modalList.value), 'initModal setEveryLoginModalStatus before');

    // 处理已领取过首充奖励的数据
    const modalItem = find(modalList.value, item => {
      return (
        activityTypeMps[item.info?.activityType] === ModalTypeEnum.DEPOSIT_BONUS &&
        item.info?.actionCfg?.activityConditions === '1'
      );
    });
    if (modalItem) {
      const state = await getDepositAwardStatus(+modalItem.id);
      console.log(state, 'getActivityList state');
      if (includes([0, 5], state)) {
        modalList.value = filter(modalList.value, item => item.id !== modalItem.id);
        setModalStatus({ type: modalItem.type, id: modalItem.id }, { forever: true });
      }
    }

    // 处理签到弹窗数据
    const signModalItem = find(modalList.value, item => {
      return item.type === ModalTypeEnum.SIGN_IN;
    });
    if (signModalItem) {
      await userStore.reqSignIn();
      console.log(signInList.value?.isSignIn, 'setEveryLoginModalStatus sign state');
      if (signInList.value?.isSignIn) {
        // modalList.value = filter(modalList.value, item => item.id !== modalItem.id);
        setModalStatus(signModalItem.type, { today: true });
      }
    }

    forEach(
      filter(modalList.value, item => !includes(defaultTypes, item.type)),
      item => {
        const postLoginModal = item.info?.actionCfg?.postLoginModal;
        const modalStatus = getModalStatus({ type: item.type, id: item.id });
        const checked = modalStatus?.forever || modalStatus?.today;
        if (checked) {
          setModalStatus({ type: item.type, id: item.id }, { popped: checked });
          item.show = !1;
        } else {
          if (postLoginModal === 'EVERY_LOGIN' || postLoginModal === 'HIGH_FREQUENCY') {
            setModalStatus({ type: item.type, id: item.id }, { popped: !1 });
            item.show = !0;
          } else if (postLoginModal === 'NO_POPUP') {
            setModalStatus({ type: item.type, id: item.id }, { popped: !0 });
            item.show = !1;
          } else if (postLoginModal === 'DAILY_ONCE') {
            const isToday = modalStatus?.date && dayjs().isSame(modalStatus.date, 'day');
            const popped = isToday ? modalStatus?.popped : !1;
            setModalStatus({ type: item.type, id: item.id }, { popped });
            item.show = !popped;
          } else {
            item.show = !modalStatus?.popped;
          }
        }
      }
    );
    console.log(cloneDeep(modalList.value), 'initModal setEveryLoginModalStatus after');
  };

  // 获取充值奖励状态
  const getDepositAwardStatus = async (id: number) => {
    try {
      if (isLogin.value) {
        const { state }: any = await getRechareInfo({ tasks_id: id });
        console.log(state, 'getBetAwardInfoData ---------');
        return Promise.resolve(state);
      } else {
        return Promise.resolve(null);
      }
    } catch (e) {
      return Promise.resolve(null);
    }
  };

  return {
    // state
    activeModal,
    showLoginRegister, // 显示登录注册
    showTopDownload, // 显示顶部下载
    showBottomDownload, // 显示底部下载
    isShowTopDownload, // 是否已显示过顶部下载
    isShowBottomDownload, // 是否已显示过底部下载
    showRegisterBonus, // 注册红包弹窗
    showDepositBonusModal, // 充值奖励弹窗
    showSignInModal, // 签到弹窗
    showLotteryWheelModal, // 幸运转盘弹窗
    showNotice, // 公告弹窗
    showHorseman, // 跑马灯
    recoverPasswordModal, // 找回密码

    // methods
    showModal,
    hideModal,
    addPopupModal,
    getPopupModalInfo,
    setModalStatus,
    getModalStatus,
    initModal,
    initModalList,
    setEveryLoginModalStatus,
    setHighFrequencyModalStatus,
  };
});
